import React, { useEffect, useRef } from 'react';
import { Alert, Button, Snackbar } from '@mui/material';
import { useRouterState } from '@tanstack/react-router';
import { useBuildVersion } from '../hooks/useBuildVersion';

interface ViewProps {
  updated: boolean;
  pathname: string;
  reload: () => void;
  /** Full page load of a path, on the new bundle. */
  load?: (path: string) => void;
}

/**
 * "FolioWorth was updated. Reload" for a tab opened before a deploy
 * (issue #35). Non-blocking; and once an update is known, the next route
 * change loads the new version instead of continuing on the old bundle.
 */
export const UpdateBannerView: React.FC<ViewProps> = ({ updated, pathname, reload, load = reload }) => {
  const seen = useRef(pathname);

  useEffect(() => {
    if (pathname === seen.current) return;
    seen.current = pathname;
    // Load the destination itself (the history entry may not be committed
    // yet when the router's state changes), on the new bundle.
    if (updated) load(pathname);
  }, [pathname, updated, load]);

  return (
    <Snackbar open={updated} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
      <Alert severity="info" variant="filled" data-testid="update-banner"
        action={<Button color="inherit" size="small" onClick={reload}>Reload</Button>}>
        FolioWorth was updated.
      </Alert>
    </Snackbar>
  );
};

const reloadPage = () => window.location.reload();
const loadPath = (path: string) => window.location.assign(path);

/** The banner wired to the version poll and the router. */
export const UpdateBanner: React.FC = () => {
  const updated = useBuildVersion();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return <UpdateBannerView updated={updated} pathname={pathname} reload={reloadPage} load={loadPath} />;
};
